/**
 * Script Execution Confirmation - Promise-based modal wrapper for bash script confirmations
 * 
 * Usage:
 *   const confirmed = await showScriptExecutionConfirmation(modifiedFiles, script);
 *   if (confirmed) { ... }
 */

import './scriptExecutionConfirmation.css';

let modalStateCallback = null;

/**
 * Register the React state setter for the confirmation modal
 * This should be called once from App.jsx on mount
 */
export function registerModalStateCallback(callback) {
  modalStateCallback = callback;
}

/**
 * Show confirmation dialog for script execution
 * Returns a Promise that resolves to true/false based on user choice
 */
export function showScriptExecutionConfirmation(modifiedFiles, script, scriptOutput = '') {
  return new Promise((resolve) => {
    if (!modalStateCallback) {
      console.error('Modal state callback not registered');
      resolve(false);
      return;
    }

    // Build detailed message about what will be changed
    const fileChangeSummary = buildFileChangeSummary(modifiedFiles);
    
    const message = (
      <div className="script-execution-confirmation">
        <div className="script-section">
          <strong>Script to execute:</strong>
          <pre className="script-preview">{script}</pre>
        </div>
        
        {scriptOutput && (
          <div className="output-section">
            <strong>Output:</strong>
            <pre className="output-preview">{scriptOutput}</pre>
          </div>
        )}
        
        <div className="changes-section">
          <strong>Files that will be modified:</strong>
          <div className="file-changes-list">
            {fileChangeSummary}
          </div>
        </div>
        
        <div className="warning-section">
          ⚠️ This action will modify {modifiedFiles.length} file{modifiedFiles.length !== 1 ? 's' : ''} in IndexedDB.
          Do you want to proceed?
        </div>
      </div>
    );

    modalStateCallback({
      isOpen: true,
      title: 'Confirm Script Execution',
      message,
      type: 'custom',
      confirmText: 'Execute & Save Changes',
      cancelText: 'Cancel',
      isDangerous: true,
      showCancel: true,
      onConfirm: () => {
        modalStateCallback({ isOpen: false });
        resolve(true);
      },
      onCancel: () => {
        modalStateCallback({ isOpen: false });
        resolve(false);
      }
    });
  });
}

/**
 * Build a formatted summary of file changes
 */
function buildFileChangeSummary(modifiedFiles) {
  return (
    <ul className="file-changes">
      {modifiedFiles.map((change, index) => {
        const icon = getChangeIcon(change.type);
        const className = `file-change file-change-${change.type}`;
        
        return (
          <li key={index} className={className}>
            <span className="change-icon">{icon}</span>
            <span className="file-path">{change.path}</span>
            <span className="change-type">({change.type})</span>
            {change.type === 'modified' && (
              <button 
                className="view-diff-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  showDiffModal(change);
                }}
              >
                View Diff
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Get icon for change type
 */
function getChangeIcon(changeType) {
  switch (changeType) {
    case 'created':
      return '✨';
    case 'modified':
      return '✏️';
    case 'deleted':
      return '🗑️';
    default:
      return '📝';
  }
}

/**
 * Show diff modal for a specific file change
 * TODO: Implement a proper diff viewer (could use react-diff-viewer)
 */
function showDiffModal(change) {
  if (!modalStateCallback) return;
  
  const diffView = (
    <div className="diff-view">
      <div className="diff-section">
        <strong>Before:</strong>
        <pre className="diff-content">{change.oldContent || '(empty)'}</pre>
      </div>
      <div className="diff-section">
        <strong>After:</strong>
        <pre className="diff-content">{change.newContent || '(deleted)'}</pre>
      </div>
    </div>
  );
  
  modalStateCallback({
    isOpen: true,
    title: `Diff: ${change.path}`,
    message: diffView,
    type: 'custom',
    confirmText: 'Close',
    showCancel: false,
    isDangerous: false,
    onConfirm: () => {
      modalStateCallback({ isOpen: false });
    }
  });
}

/**
 * Show a simple info message (non-blocking)
 */
export function showScriptExecutionInfo(title, message, isDangerous = false) {
  if (!modalStateCallback) {
    console.error('Modal state callback not registered');
    return;
  }

  modalStateCallback({
    isOpen: true,
    title,
    message,
    type: 'confirm',
    confirmText: 'OK',
    showCancel: false,
    isDangerous,
    onConfirm: () => {
      modalStateCallback({ isOpen: false });
    }
  });
}

/**
 * Show error message
 */
export function showScriptExecutionError(error) {
  showScriptExecutionInfo(
    'Script Execution Failed',
    <div>
      <p>An error occurred while executing the script:</p>
      <pre className="error-message">{error}</pre>
    </div>,
    true
  );
}
